<template>
  <div class="map-elem-info junkai_tenken_report_tenken_data">
    <div class="col-icon">
      <img class="resource-icon" :src="iconPath">
    </div>
    <div class="col-info">
      <div class="row1 mb6">
        <div class="ft-bold">
          <span class="ib lbl">点検日時: </span>
          <span class="ib val">{{state.tenkenData.inspectionDate}}</span>
          <span class="ib val ml6">{{state.tenkenData.inspectionTime}}</span>
        </div>
        <div class="move-buttons mt2">
          <button class="btn btn-primary btn-xs mr4" @click="showPrevious">
            <i class="fas fa-arrow-left"></i>
            前へ
          </button>
          <button class="btn btn-primary btn-xs" @click="showNext">
            次へ
            <i class="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>
      <div class="row2">
        <div>
          <span class="lbl">点検区分: </span>
          <span class="val">{{ state.tenkenData.inspectionType }}</span>
          <span class="lbl ml12">判定: </span>
          <span class="val">{{ state.tenkenData.damageRank }}</span>
        </div>
      </div>
      <div class="row3 mb6">
        <div>
          <span class="ib lbl">場所: </span>
          <span class="ib val">{{landmarkDisp}}</span>
        </div>
      </div>
      <div class="row4 mb6">
        <div class="image-preview-area">
          <div class="image-box" v-if="savedImage">
            <img :src="savedImage" class="image" @click="showImage"/>
          </div>
        </div>
        <div class="buttons ta-right mr8" v-if="!!state.tenkenData.tenkenDataLink">
          <button class="btn btn-sm btn-info" @click="openJunkaiTenkenReport">
            巡回点検報告書を開く
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  onMounted,
  watch,
  computed,
  reactive,
} from 'vue';
import extJunkaiTenkenReportTenkenDataApi from 'src/apis/extJunkaiTenkenReportTenkenData';
import type { MapElemInfo } from 'src/models';
import useMapElemInfoImagePreview from 'src/composables/useMapElemInfoImagePreview';
import { dtFormat } from 'src/lib/dateHelper';
import type { GITenkenData } from 'src/models/geoItem';

interface MapElemInfoJunkaiTenkenReportTenkenDataState {
  tenkenData: GITenkenData;
}

interface Props {
  elem: MapElemInfo<GITenkenData>;
}

const props = withDefaults(defineProps<Props>(), {});

const emit = defineEmits<{
  (e: string, ...args: unknown[]): void;
}>();

const state = reactive<MapElemInfoJunkaiTenkenReportTenkenDataState>({
  tenkenData: props.elem.data,
});
const skipFetchingImage = computed<boolean>(() => {
  return !state.tenkenData?.photoIds || state.tenkenData.photoIds.length === 0;
});
const landmarkDisp = computed<string>(() => [
  state.tenkenData.roadName,
  state.tenkenData.direction,
  state.tenkenData.placeName,
  state.tenkenData.landmarkNumber,
].filter(e => !!e).join(' '));
const modalTitle = computed<string>(() => `${state.tenkenData.inspectionDate} ${state.tenkenData.inspectionTime} - ${landmarkDisp.value}`);
const downloadFilenameTemplate = computed<string>(() => {
  const tenkenDataTs = dtFormat(state.tenkenData.inspectionDate, 'yyyymmddHHMMSS');
  return `${state.tenkenData.id}_${tenkenDataTs}_$TIMESTAMP.jpg`;
});
const { showPrevious, showNext, savedImage, showImage } = useMapElemInfoImagePreview({
  emit,
  elem: computed(() => props.elem),
  refMapElemInfoData: computed(() => state.tenkenData),
  skipFetchingImage,
  fetchApiFn: () => {
    // photoIdsから最初の1件のみ画面に表示する
    return extJunkaiTenkenReportTenkenDataApi.getImage(state.tenkenData.photoIds[0]);
  },
  modalTitle,
  downloadFilenameTemplate,
});

const initDisplay = () => {
  state.tenkenData = props.elem.data;
};

onMounted(initDisplay);

watch(() => props.elem, initDisplay);

const iconPath = '/static/img/junkai_tenken_report_pin.png';
const openJunkaiTenkenReport = () => {
  window.open(state.tenkenData.tenkenDataLink, '_blank');
};
</script>

<style lang="scss" scoped>
.map-elem-info {
  .col-icon {
    margin-right: 4px;
    display: flex;
    flex-direction: column;
    .resource-icon {
      width: 42px;
      &.start {
        margin: -4px 0 0 0;
      }
      &.end {
        margin: -30px 0 0 0;
      }
    }
  }
  .col-info {
    .row1, .row2, .row3, .row4 {
      display: flex;
      justify-content: space-between;
    }
    .row1 {
      flex-wrap: wrap;
    }
    .lbl {
      margin-right: 4px;
    }
    .left-side {
      flex-grow: 1;
    }
    .buttons {
      width: 152px;
      min-width: 152px;
      align-self: flex-end;
    }
    .image-preview-area {
      display: flex;
      width: 100%;
      .image-box {
        cursor: pointer;
        position: relative;
        border: 1px solid #dddddd;
        margin-left: 6px;
        .image {
          max-height: 72px;
          max-width: 128px;
        }
      }
    }
  }
}
</style>
